/**
 * Generate React MCP Manifest from the Web Component MCP manifest.
 *
 * Reads mcp-manifest.json (generated by generate-mcp-manifest.ts) and
 * transforms it into react-manifest.json, which the cre8-mcp server
 * resolves via `@tmorrow/cre8-wc/react-manifest.json` when tools are
 * called with `format: "react"`.
 *
 * Naming rules intentionally mirror scripts/generate-react-wrappers.ts
 * and the cre8-mcp server's own web→react tag conversion:
 *   - cre8-button        → Cre8Button
 *   - kebab-case attrs   → camelCase props
 *   - events             → on<EventName> callback props
 *   - slots              → children?: React.ReactNode
 *
 * Run after build:mcp-manifest:
 *   npx tsx scripts/generate-react-manifest.ts
 */

import { readFileSync, writeFileSync } from 'fs';
import { join, dirname } from 'path';
import { fileURLToPath } from 'url';

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);
const ROOT = join(__dirname, '..');

// ─── Types (input: mcp-manifest.json) ────────────────────────────────
interface McpAttribute {
  type: string;
  values?: string[];
  default?: string | boolean | number;
  description?: string;
}

interface McpComponent {
  name: string;
  category: string;
  description: string;
  attributes: Record<string, McpAttribute>;
  properties: Record<string, McpAttribute>;
  slots: Record<string, { description?: string }>;
  events: Record<string, { detail?: Record<string, unknown>; description?: string }>;
  cssProperties: Record<string, { description?: string }>;
  examples: Array<{ description: string; html: string }>;
}

interface McpManifest {
  version: string;
  library: string;
  tagPrefix: string;
  description: string;
  framework: string;
  components: McpComponent[];
  baseClasses: unknown;
  patterns: Array<{ name: string; description: string; template: string }>;
  designTokens: unknown;
  accessibility: unknown;
  dependencies: Record<string, string>;
}

// ─── Types (output: react-manifest.json) ─────────────────────────────
interface ReactProp {
  type: string;
  values?: string[];
  default?: string | boolean | number;
  description?: string;
}

interface ReactComponent {
  name: string;
  tagName: string;
  category: string;
  description: string;
  props: Record<string, ReactProp>;
  examples: Array<{ description: string; jsx: string }>;
}

// ─── Naming helpers ──────────────────────────────────────────────────

/** cre8-button → Cre8Button (same rule as generate-react-wrappers.ts) */
function toPascalCase(tagName: string): string {
  return tagName
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

/** full-width → fullWidth; already-camel names pass through */
function toCamelCase(name: string): string {
  return name.includes('-')
    ? name.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
    : name;
}

/** cre8.dropdown-close → onCre8DropdownClose (same rule as wrappers) */
function toEventPropName(eventName: string): string {
  const valid = eventName
    .split(/[.-]/)
    .map((part, index) =>
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join('');
  return 'on' + valid.charAt(0).toUpperCase() + valid.slice(1);
}

// ─── HTML example → JSX ──────────────────────────────────────────────

/**
 * Best-effort conversion of a web-component HTML snippet to JSX:
 *   - cre8-* tags → Cre8* component names
 *   - kebab-case attributes → camelCase
 *   - class → className
 *   - HTML comments → JSX comments
 *   - empty elements collapsed to self-closing form
 */
function htmlToJsx(html: string): string {
  let jsx = html;

  // Tag names: <cre8-button ...> / </cre8-button>
  jsx = jsx.replace(/<(\/?)(cre8-[a-z0-9-]+)/g, (_m, slash: string, tag: string) => {
    return `<${slash}${toPascalCase(tag)}`;
  });

  // Attributes inside tags: kebab-case → camelCase, class → className.
  // `slot` is left as-is (passed through to the underlying element).
  jsx = jsx.replace(/<[^/!][^>]*>/g, (tagStr) =>
    tagStr.replace(/\s([a-z][a-z0-9-]*)(=)?/g, (m, attr: string, eq?: string) => {
      if (attr === 'class') return ` className${eq || ''}`;
      if (attr.includes('-')) return ` ${toCamelCase(attr)}${eq || ''}`;
      return m;
    }),
  );

  // HTML comments → JSX comments
  jsx = jsx.replace(/<!--([\s\S]*?)-->/g, '{/*$1*/}');

  // Collapse empty elements: <Cre8Button ... ></Cre8Button> → <Cre8Button ... />
  jsx = jsx.replace(/<(Cre8[A-Za-z0-9]+)([^>]*)><\/\1>/g, '<$1$2 />');

  return jsx;
}

// ─── Transform ───────────────────────────────────────────────────────

function transformComponent(comp: McpComponent): ReactComponent {
  const props: Record<string, ReactProp> = {};

  // Attributes → camelCase props
  for (const [attrName, attr] of Object.entries(comp.attributes || {})) {
    props[toCamelCase(attrName)] = { ...attr };
  }

  // Properties are already camelCase; merge (attributes win on collision,
  // since the wrapper exposes the attribute-backed prop)
  for (const [propName, prop] of Object.entries(comp.properties || {})) {
    if (!(propName in props)) {
      props[propName] = { ...prop };
    }
  }

  // Slots → children
  if (Object.keys(comp.slots || {}).length > 0) {
    const named = Object.keys(comp.slots).filter((s) => s !== 'default');
    props.children = {
      type: 'React.ReactNode',
      description:
        named.length > 0
          ? `Slotted content. Named slots (set slot="..." on the child): ${named.join(', ')}`
          : 'Slotted content.',
    };
  }

  // Events → on<EventName> callback props
  for (const [eventName, event] of Object.entries(comp.events || {})) {
    props[toEventPropName(eventName)] = {
      type: '(event: CustomEvent) => void',
      ...(event.description ? { description: event.description } : {}),
    };
  }

  return {
    name: toPascalCase(comp.name),
    tagName: comp.name,
    category: comp.category,
    description: comp.description,
    props,
    examples: (comp.examples || []).map((ex) => ({
      description: ex.description,
      jsx: htmlToJsx(ex.html),
    })),
  };
}

// ─── Main ────────────────────────────────────────────────────────────

function main() {
  const inputPath = join(ROOT, 'mcp-manifest.json');
  console.log(`Reading web manifest from: ${inputPath}`);

  const webManifest: McpManifest = JSON.parse(readFileSync(inputPath, 'utf-8'));

  const components = webManifest.components.map(transformComponent);
  console.log(`Transformed ${components.length} components`);

  const reactManifest = {
    version: webManifest.version,
    library: '@tmorrow/cre8-react',
    componentPrefix: 'Cre8',
    description: 'React wrappers for cre8 Web Components (@tmorrow/cre8-wc)',
    framework: 'React 18+ via @lit/react',
    components,
    baseClasses: webManifest.baseClasses,
    patterns: (webManifest.patterns || []).map((p) => ({
      ...p,
      template: htmlToJsx(p.template),
    })),
    designTokens: webManifest.designTokens,
    accessibility: webManifest.accessibility,
    dependencies: {
      '@lit/react': webManifest.dependencies?.['@lit/react'] || '^1.0.8',
      '@tmorrow/cre8-wc': `^${webManifest.version}`,
      react: '>=18',
      'react-dom': '>=18',
    },
  };

  const outputPath = join(ROOT, 'react-manifest.json');
  writeFileSync(outputPath, JSON.stringify(reactManifest, null, 2) + '\n');
  console.log(`Wrote react manifest to: ${outputPath}`);
  console.log(`Total components: ${components.length}`);
}

main();
